import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  TextField as MUITextField,
  Box,
  Badge,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";

import { useSelector, shallowEqual } from "react-redux";
import { useFormikContext, Field } from "formik";
import { TextField } from "formik-material-ui";
import { DebouncedTextField } from "./DebouncedTextField";
import { MultiSelectChips } from "./MultiSelectChips";
import { WatchFormState } from "../../../state/watchform/state";
import { AppState, WatchSelectors } from "../../../state";

export const Case = (props) => {
  const selectStyles = {
    fontSize: '12px',
    height: '24px',
    padding: '2px 12px 2px 5px',
    border: '1px solid rgba(0, 0, 0, 0.23)',
    backgroundColor: 'transparent',
    width: '100%',
  };

  const { classes } = props;
  const {
    values,
    setFieldValue,
  } = useFormikContext<WatchFormState>();

  const [waterResistAtm, setWaterResistAtm] = useState<undefined | number | null>(
    values?.case_atm
      ? parseFloat(values.case_atm as any)
      : values?.water_resistance_m
      ? // @ts-ignore: Object is possibly 'null'.
        values.water_resistance_m / 10
      : null,
  );
  const [waterResistM, setWaterResistM] = useState<undefined | number | null>(
    values?.water_resistance_m
      ? // @ts-ignore: Object is possibly 'null'.
        values.water_resistance_m
      : // @ts-ignore: Object is possibly 'null'.
      values?.water_resistance_ft
      ? // @ts-ignore: Object is possibly 'null'.
        Math.round(values?.water_resistance_ft / 3.3)
      : null,
  );
  const [waterResistFt, setWaterResistFt] = useState<undefined | number | null>(
    values.water_resistance_ft || null,
  );
  const [case_shape_category, setCase_shape_category] = useState();
  const [showButtonMaterial, setShowButtonMaterial] = useState(false);

  const {
    case_finish: case_finish_list,
    bezel: bezel_list,
    bezel_material: bezel_material_list,
    bezel_finish: bezel_finish_list,
    bezel_characteristics: bezel_characteristics_list,
    bezel_color: bezel_color_list,
    crystal: crystal_list,
    case_shape: case_shape_list,
    case_back: case_back_list,
    material: material_list,
  } = useSelector((state: AppState) => state.watches.watchLists, shallowEqual);

  const handleWaterResistChangeAtm = (event) => {
    const wra = event.target.value;
    setWaterResistAtm(wra || null);
    setWaterResistM(wra * 10 || null);
    setWaterResistFt(wra * 33 || null);
  };
  const handleWaterResistChangeM = (event) => {
    const wrm = event.target.value;
    setWaterResistM(wrm || null);
    setWaterResistAtm(wrm / 10 || null);
    setWaterResistFt(wrm * 3.3 || null);
  };
  const handleWaterResistChangeFt = (event) => {
    const wrf = event.target.value;
    setWaterResistFt(wrf || null);
  };
  const handleWaterResistBlurFt = (event) => {
    if (!waterResistM) {
      const meters = Math.floor(event.target.value / 3.3);
      setWaterResistM(meters || null);
      setWaterResistAtm(meters / 10 || null);
    }
  };

  const handleChange = (event) => {
    setFieldValue("button_notes", "");
    setFieldValue("screw_down_buttons", false);
    setFieldValue("button_material_id", null);
    setShowButtonMaterial(!showButtonMaterial);
  };

  useEffect(() => {
    setFieldValue("water_resistance_ft", waterResistFt);
    setFieldValue("water_resistance_m", waterResistM);
    setFieldValue("case_atm", waterResistAtm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waterResistM, waterResistFt, waterResistAtm]);

  useEffect(() => {
    if (values.case_shape_id && (case_shape_list as any[]).length > 0) {
      const shape = (case_shape_list as any[]).find(
        (x) => Number(x.case_shape_id) === Number(values.case_shape_id)
      );
      if (shape?.category) setCase_shape_category(shape.category);
      else setCase_shape_category(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [case_shape_list, values.case_shape_id]);

  const siteId = useSelector((state: AppState) => state.scopes.site_id);

  return (
    <Accordion expanded={true} style={{ width: "100%" }}>
      <AccordionSummary
        style={{ cursor: "default" }}
        aria-controls="panel1a-content"
        id="panel1a-header"
        tabIndex={-1}
      >
        <Typography>
          <Badge badgeContent={props.requiredCaseBadge} color="secondary">
            Case Description
          </Badge>
        </Typography>
      </AccordionSummary>
      <AccordionDetails className={classes.grid} style={{ display: "grid" }}>
        <Box display="flex" flexWrap="wrap" flexDirection="column" alignItems="left">

          {/* ROW 0: General Case Notes (full width) */}
          <Box display="flex" alignItems="center" mx={1} mb={1} style={{ gap: 16 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
              <label style={{ fontSize: 11 }}>General Case Notes:</label>
              <DebouncedTextField
                className={
                  siteId !== 0
                    ? classes.gridTwoBlack
                    : values.general_case_notes
                    ? classes.gridTwoBlue
                    : classes.gridTwo
                }
                name="general_case_notes"
                style={{ width: "100%" }}
                multiline
              />
            </div>
          </Box>

          {/* ROW 1: Case Material | Case Finish | Case Finish Notes | Bezel Type | Bezel Material */}
          <Box display="flex" alignItems="center" mx={1} mb={1} style={{ gap: 16 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "16%" }}>
              <label style={{ fontSize: 11 }}>Case Material:</label>
              <select
                disabled={siteId !== 0}
                value={values.case_material_id || ""}
                onChange={(e) => setFieldValue("case_material_id", e.target.value ? Number(e.target.value) : null)}
                className={
                  siteId !== 0
                    ? `${classes.gridTwoBlack} custom-select`
                    : values.case_material_id
                    ? `${classes.gridTwoBlue} custom-select`
                    : `${classes.gridTwo} custom-select`
                }
                style={selectStyles}
              >
                <option value=""></option>
                {(material_list as any[])
                  .filter((m: any) => m.brand_id === values.brand_id || m.brand_id === null)
                  .sort((a: any, b: any) => (a.case_material || "").localeCompare(b.case_material || ""))
                  .map((m: any) => (
                    <option key={m.case_material_id} value={m.case_material_id}>{m.case_material}</option>
                  ))}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "10%" }}>
              <label style={{ fontSize: 11 }}>Case Color:</label>
              {/* Shared-pool field: stores in item.case_color_id but the options
                  come from the Bezel Color pool (item_bezel_color). */}
              <select
                disabled={siteId !== 0}
                value={values.case_color_id || ""}
                onChange={(e) => setFieldValue("case_color_id", e.target.value ? Number(e.target.value) : null)}
                className={
                  siteId !== 0
                    ? `${classes.gridTwoBlack} custom-select`
                    : values.case_color_id
                    ? `${classes.gridTwoBlue} custom-select`
                    : `${classes.gridTwo} custom-select`
                }
                style={selectStyles}
              >
                <option value=""></option>
                {(bezel_color_list as any[] || [])
                  .sort((a: any, b: any) => (a.bezel_color || "").localeCompare(b.bezel_color || ""))
                  .map((c: any) => (
                    <option key={c.bezel_color_id} value={c.bezel_color_id}>{c.bezel_color}</option>
                  ))}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "10%" }}>
              <label style={{ fontSize: 11 }}>Case Finish:</label>
              <select
                disabled={siteId !== 0}
                value={values.case_finish_id || ""}
                onChange={(e) => setFieldValue("case_finish_id", e.target.value ? Number(e.target.value) : null)}
                className={
                  siteId !== 0
                    ? `${classes.gridTwoBlack} custom-select`
                    : values.case_finish_id
                    ? `${classes.gridTwoBlue} custom-select`
                    : `${classes.gridTwo} custom-select`
                }
                style={selectStyles}
              >
                <option value=""></option>
                {(case_finish_list as any[] || [])
                  .sort((a: any, b: any) => (a.case_finish || "").localeCompare(b.case_finish || ""))
                  .map((f: any) => (
                    <option key={f.case_finish_id} value={f.case_finish_id}>{f.case_finish}</option>
                  ))}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "61.5%" }}>
              <label style={{ fontSize: 11 }}>Case Finish Notes:</label>
              <DebouncedTextField
                className={
                  siteId !== 0
                    ? classes.gridTwoBlack
                    : values.case_finish_notes
                    ? classes.gridTwoBlue
                    : classes.gridTwo
                }
                name="case_finish_notes"
                style={{ width: "100%" }}
                multiline
              />
            </div>
            </Box>
          {/* ROW 2: Bezel Color | Bezel Finish | Bezel Characteristics (multi) | Bezel Notes | Crown Material | Screw Down Crown | Crown Notes */}
          <Box display="flex" alignItems="center" mx={1} mb={1} style={{ gap: 16 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "14%" }}>
              <label style={{ fontSize: 11 }}>Bezel Type:</label>
              <select
                disabled={siteId !== 0}
                value={values.bezel_id || ""}
                onChange={(e) => setFieldValue("bezel_id", e.target.value ? Number(e.target.value) : null)}
                className={
                  siteId !== 0
                    ? `${classes.gridTwoBlack} custom-select`
                    : values.bezel_id
                    ? `${classes.gridTwoBlue} custom-select`
                    : `${classes.gridTwo} custom-select`
                }
                style={selectStyles}
              >
                <option value=""></option>
                {(bezel_list as any[] || [])
                  .sort((a: any, b: any) => (a.bezel || "").localeCompare(b.bezel || ""))
                  .map((b: any) => (
                    <option key={b.bezel_id} value={b.bezel_id}>{b.bezel}</option>
                  ))}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "22%" }}>
              <label style={{ fontSize: 11 }}>Bezel Material:</label>
              <MultiSelectChips
                disabled={siteId !== 0}
                value={values.bezel_material_ids || []}
                onChange={(ids: any) => setFieldValue("bezel_material_ids", ids)}
                options={(material_list as any[] || []).filter(
                  (m: any) => m.brand_id === values.brand_id || m.brand_id === null,
                )}
                getOptionValue={(m: any) => m.case_material_id}
                getOptionLabel={(m: any) => m.case_material}
                className={
                  siteId !== 0
                    ? `${classes.gridTwoBlack} custom-select`
                    : values.bezel_material_ids?.length
                    ? `${classes.gridTwoBlue} custom-select`
                    : `${classes.gridTwo} custom-select`
                }
                style={selectStyles}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "8%" }}>
              <label style={{ fontSize: 11 }}>Bezel Color:</label>
              <select
                disabled={siteId !== 0}
                value={values.bezel_color_id || ""}
                onChange={(e) => setFieldValue("bezel_color_id", e.target.value ? Number(e.target.value) : null)}
                className={
                  siteId !== 0
                    ? `${classes.gridTwoBlack} custom-select`
                    : values.bezel_color_id
                    ? `${classes.gridTwoBlue} custom-select`
                    : `${classes.gridTwo} custom-select`
                }
                style={selectStyles}
              >
                <option value=""></option>
                {(bezel_color_list as any[] || [])
                  .sort((a: any, b: any) => (a.bezel_color || "").localeCompare(b.bezel_color || ""))
                  .map((c: any) => (
                    <option key={c.bezel_color_id} value={c.bezel_color_id}>{c.bezel_color}</option>
                  ))}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "12%" }}>
              <label style={{ fontSize: 11 }}>Bezel Finish:</label>
              <select
                disabled={siteId !== 0}
                value={values.bezel_finish_id || ""}
                onChange={(e) => setFieldValue("bezel_finish_id", e.target.value ? Number(e.target.value) : null)}
                className={
                  siteId !== 0
                    ? `${classes.gridTwoBlack} custom-select`
                    : values.bezel_finish_id
                    ? `${classes.gridTwoBlue} custom-select`
                    : `${classes.gridTwo} custom-select`
                }
                style={selectStyles}
              >
                <option value=""></option>
                {(case_finish_list as any[] || [])
                  .sort((a: any, b: any) => (a.case_finish || "").localeCompare(b.case_finish || ""))
                  .map((f: any) => (
                    <option key={f.case_finish_id} value={f.case_finish_id}>{f.case_finish}</option>
                  ))}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "16%" }}>
              <label style={{ fontSize: 11 }}>Bezel Characteristics:</label>
              <MultiSelectChips
                disabled={siteId !== 0}
                value={values.bezel_characteristics_ids || []}
                onChange={(ids) => setFieldValue("bezel_characteristics_ids", ids)}
                options={bezel_characteristics_list as any[] || []}
                getOptionValue={(bc) => bc.bezel_characteristics_id}
                getOptionLabel={(bc) => bc.bezel_characteristics}
                className={
                  siteId !== 0
                    ? classes.gridTwoBlack
                    : (values.bezel_characteristics_ids || []).length > 0
                    ? classes.gridTwoBlue
                    : classes.gridTwo
                }
                style={selectStyles}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "21%" }}>
              <label style={{ fontSize: 11 }}>Bezel Notes:</label>
              <DebouncedTextField
                className={values.bezel_notes ? classes.gridTwoBlue : classes.gridTwo}
                name="bezel_notes"
                style={{ width: "100%" }}
                multiline
              />
            </div>
          </Box>

          <Box mx={1} mb={1} display="flex" justifyContent="space-between" alignItems="center">
          </Box>

          {/** ROW 3: CROWN MATERIAL |            */}
          <Box display="flex" alignItems="center" mx={1} mb={1} style={{ gap: 16 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "22%" }}>
                <label style={{ fontSize: 11 }}>Crown Material:</label>
                <MultiSelectChips
                  disabled={siteId !== 0}
                  value={values.crown_material_ids || []}
                  onChange={(ids: any) => setFieldValue("crown_material_ids", ids)}
                  options={(material_list as any[] || []).filter(
                    (m: any) => m.brand_id === values.brand_id || m.brand_id === null,
                  )}
                  getOptionValue={(m: any) => m.case_material_id}
                  getOptionLabel={(m: any) => m.case_material}
                  className={
                    siteId !== 0
                      ? `${classes.gridTwoBlack} custom-select`
                      : values.crown_material_ids?.length
                      ? `${classes.gridTwoBlue} custom-select`
                      : `${classes.gridTwo} custom-select`
                  }
                  style={selectStyles}
                />
              </div>
              <Box style={{ width: "6%" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!values.screw_down_crown}
                      disabled={siteId !== 0}
                      onChange={(e) => setFieldValue("screw_down_crown", e.target.checked)}
                      style={{ padding: 0 }}
                    />
                  }
                  label={<span style={{ fontSize: 11 }}>S. D. Crown:</span>}
                  labelPlacement="top"
                  style={{ margin: 0, gap: 0 }}
                />
              </Box>
              <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "20%" }}>
                <label style={{ fontSize: 11 }}>Crown Notes:</label>
                <DebouncedTextField
                  className={
                    siteId !== 0
                      ? classes.gridTwoBlack
                      : values.crown_notes
                      ? classes.gridTwoBlue
                      : classes.gridTwo
                  }
                  disabled={siteId !== 0}
                  name="crown_notes"
                  style={{ width: "100%" }}
                  multiline
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "15%" }}>
                    <label style={{ fontSize: 11 }}>Button Material:</label>
                    <select
                      disabled={siteId !== 0}
                      value={values.button_material_id || ""}
                      onChange={(e) => setFieldValue("button_material_id", e.target.value ? Number(e.target.value) : null)}
                      className={
                        siteId !== 0
                          ? `${classes.gridTwoBlack} custom-select`
                          : values.button_material_id
                          ? `${classes.gridTwoBlue} custom-select`
                          : `${classes.gridTwo} custom-select`
                      }
                      style={selectStyles}
                    >
                      <option value=""></option>
                      {(material_list as any[])
                        .filter((m: any) => m.brand_id === values.brand_id || m.brand_id === null)
                        .sort((a: any, b: any) => (a.case_material || "").localeCompare(b.case_material || ""))
                        .map((m: any) => (
                          <option key={m.case_material_id} value={m.case_material_id}>{m.case_material}</option>
                        ))}
                    </select>
                  </div>
                  <Box style={{ width: "7%" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!values.screw_down_buttons}
                          disabled={siteId !== 0}
                          onChange={(e) => setFieldValue("screw_down_buttons", e.target.checked)}
                          style={{ padding: 0 }}
                        />
                      }
                      label={<span style={{ fontSize: 11 }}>S. D. Buttons:</span>}
                      labelPlacement="top"
                      style={{ margin: 0, gap: 0 }}
                    />
                  </Box>
                  <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "30%" }}>
                    <label style={{ fontSize: 11 }}>Button Notes:</label>
                    <DebouncedTextField
                      className={
                        siteId !== 0
                          ? classes.gridTwoBlack
                          : values.button_notes
                          ? classes.gridTwoBlue
                          : classes.gridTwo
                      }
                      disabled={siteId !== 0}
                      name="button_notes"
                      style={{ width: "100%" }}
                      multiline
                    />
                  </div>
          </Box>

          {/* ROW 4: Crystal | Crystal Notes | Case Back | Case Back Notes */}
          <Box display="flex" alignItems="center" mx={1} mb={1} style={{ gap: 16 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "17%" }}>
                <label style={{ fontSize: 11 }}>Crystal:</label>
                <select
                  disabled={siteId !== 0}
                  value={values.crystal_id || ""}
                  onChange={(e) => setFieldValue("crystal_id", e.target.value ? Number(e.target.value) : null)}
                  className={
                    siteId !== 0
                      ? `${classes.gridTwoBlack} custom-select`
                      : values.crystal_id
                      ? `${classes.gridTwoBlue} custom-select`
                      : `${classes.gridTwo} custom-select`
                  }
                  style={selectStyles}
                >
                  <option value=""></option>
                  {(crystal_list as any[] || [])
                    .sort((a: any, b: any) => (a.crystal || "").localeCompare(b.crystal || ""))
                    .map((c: any) => (
                      <option key={c.crystal_id} value={c.crystal_id}>{c.crystal}</option>
                    ))}
                </select>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "34%" }}>
                <label style={{ fontSize: 11 }}>Crystal Notes:</label>
                <DebouncedTextField
                  className={
                    siteId !== 0
                      ? classes.gridTwoBlack
                      : values.crystal_notes
                      ? classes.gridTwoBlue
                      : classes.gridTwo
                  }
                  disabled={siteId !== 0}
                  name="crystal_notes"
                  style={{ width: "100%" }}
                  multiline
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "8%" }}>
                <label style={{ fontSize: 11 }}>Case Back:</label>
                <select
                  disabled={siteId !== 0}
                  value={values.case_back_id || ""}
                  onChange={(e) => setFieldValue("case_back_id", e.target.value ? Number(e.target.value) : null)}
                  className={
                    siteId !== 0
                      ? `${classes.gridTwoBlack} custom-select`
                      : values.case_back_id
                      ? `${classes.gridTwoBlue} custom-select`
                      : `${classes.gridTwo} custom-select`
                  }
                  style={selectStyles}
                >
                  <option value=""></option>
                  {(case_back_list as any[] || [])
                    .sort((a: any, b: any) => (a.case_back || "").localeCompare(b.case_back || ""))
                    .map((cb: any) => (
                      <option key={cb.case_back_id} value={cb.case_back_id}>{cb.case_back}</option>
                    ))}
                </select>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "37%" }}>
                <label style={{ fontSize: 11 }}>Case Back Notes:</label>
                <DebouncedTextField
                  className={
                    siteId !== 0
                      ? classes.gridTwoBlack
                      : values.case_back_notes
                      ? classes.gridTwoBlue
                      : classes.gridTwo
                  }
                  disabled={siteId !== 0}
                  name="case_back_notes"
                  style={{ width: "100%" }}
                  multiline
                />
              </div>
            </Box>

          {/* ROW 6: Case Shape | Width, Height, Diam | Lug, Lug Width Dimension Notes */}
            <Box display="flex" alignItems="center" mx={1} mb={1} style={{ gap: 16 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "8%" }}>
                <label style={{ fontSize: 11 }}>Case Shape:</label>
                <select
                  disabled={siteId !== 0}
                  value={values.case_shape_id || ""}
                  onChange={(e) => {
                    const shapeId = e.target.value ? Number(e.target.value) : null;
                    setFieldValue("case_shape_id", shapeId);
                    const shape = (case_shape_list as any[]).find((x) => Number(x.case_shape_id) === Number(shapeId));
                    setCase_shape_category(shape?.category ?? undefined);
                  }}
                  className={
                    siteId !== 0
                      ? `${classes.gridTwoBlack} custom-select`
                      : values.case_shape_id
                      ? `${classes.gridTwoBlue} custom-select`
                      : `${classes.gridTwo} custom-select`
                  }
                  style={selectStyles}
                >
                  <option value=""></option>
                  {(case_shape_list as any[])
                    .sort((a: any, b: any) => (a.case_shape || "").localeCompare(b.case_shape || ""))
                    .map((s: any) => (
                      <option key={s.case_shape_id} value={s.case_shape_id}>{s.case_shape}</option>
                    ))}
                </select>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "4%" }}>
                <label style={{ fontSize: 11 }}>Diam.:</label>
                <Field
                  disabled={siteId !== 0 || case_shape_category !== "Diameter"}
                  className={
                    siteId !== 0 ? classes.gridTwoBlack
                      : case_shape_category !== "Diameter" ? classes.gridTwoGrey
                      : values.case_diameter ? classes.gridTwoBlue : classes.gridTwo
                  }
                  onKeyDown={(e: React.KeyboardEvent) => { if (!/[\d.]/.test(e.key) && !['Backspace','Delete','Tab','Escape','Enter','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(e.key)) e.preventDefault(); }}
                  component={TextField}
                  name="case_diameter"
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  style={{ width: "100%" }}
                  multiline
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "4%" }}>
                <label style={{ fontSize: 11 }}>Width:</label>
                <Field
                  disabled={siteId !== 0 || (case_shape_category !== "Width" && case_shape_category !== "Width+Height")}
                  className={
                    siteId !== 0 ? classes.gridTwoBlack
                      : (case_shape_category !== "Width" && case_shape_category !== "Width+Height") ? classes.gridTwoGrey
                      : values.case_width ? classes.gridTwoBlue : classes.gridTwo
                  }
                  onKeyDown={(e: React.KeyboardEvent) => { if (!/[\d.]/.test(e.key) && !['Backspace','Delete','Tab','Escape','Enter','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(e.key)) e.preventDefault(); }}
                  component={TextField}
                  name="case_width"
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  style={{ width: "100%" }}
                  multiline
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "4%" }}>
                <label style={{ fontSize: 11 }}>Height:</label>
                <Field
                  disabled={siteId !== 0 || case_shape_category !== "Width+Height"}
                  className={
                    siteId !== 0 ? classes.gridTwoBlack
                      : case_shape_category !== "Width+Height" ? classes.gridTwoGrey
                      : values.case_length ? classes.gridTwoBlue : classes.gridTwo
                  }
                  onKeyDown={(e: React.KeyboardEvent) => { if (!/[\d.]/.test(e.key) && !['Backspace','Delete','Tab','Escape','Enter','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(e.key)) e.preventDefault(); }}
                  component={TextField}
                  name="case_length"
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  style={{ width: "100%" }}
                  multiline
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "4%" }}>
                <label style={{ fontSize: 11 }}>Thickness:</label>
                <Field
                  className={
                    siteId !== 0
                      ? classes.gridTwoBlack
                      : values.case_thickness ? classes.gridTwoBlue : classes.gridTwo
                  }
                  disabled={siteId !== 0}
                  onKeyDown={(e: React.KeyboardEvent) => { if (!/[\d.]/.test(e.key) && !['Backspace','Delete','Tab','Escape','Enter','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(e.key)) e.preventDefault(); }}
                  component={TextField}
                  name="case_thickness"
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  style={{ width: "100%" }}
                  multiline
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "7%" }}>
                <label style={{ fontSize: 11 }}>Lug to Lug:</label>
                <Field
                  className={
                    siteId !== 0
                      ? classes.gridTwoBlack
                      : values.height_and_lugs ? classes.gridTwoBlue : classes.gridTwo
                  }
                  disabled={siteId !== 0}
                  onKeyDown={(e: React.KeyboardEvent) => { if (!/[\d.]/.test(e.key) && !['Backspace','Delete','Tab','Escape','Enter','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(e.key)) e.preventDefault(); }}
                  component={TextField}
                  name="height_and_lugs"
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  style={{ width: "100%" }}
                  multiline
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "5%" }}>
                <label style={{ fontSize: 11 }}>Lug Width:</label>
                <Field
                  className={
                    siteId !== 0
                      ? classes.gridTwoBlack
                      : values.lug_width ? classes.gridTwoBlue : classes.gridTwo
                  }
                  disabled={siteId !== 0}
                  onKeyDown={(e: React.KeyboardEvent) => { if (!/[\d.]/.test(e.key) && !['Backspace','Delete','Tab','Escape','Enter','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(e.key)) e.preventDefault(); }}
                  component={TextField}
                  name="lug_width"
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  style={{ width: "100%" }}
                  multiline
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "54%" }}>
                <label style={{ fontSize: 11 }}>Dimensions Notes:</label>
                <DebouncedTextField
                  className={
                    siteId !== 0
                      ? classes.gridTwoBlack
                      : values.dimension_notes ? classes.gridTwoBlue : classes.gridTwo
                  }
                  disabled={siteId !== 0}
                  name="dimension_notes"
                  style={{ width: "100%" }}
                  multiline
                />
              </div>
            </Box>

          {/* ROW 7: Water Resistance */}
          <Box display="flex" alignItems="center" mx={1} mb={1} style={{ gap: 16 }}>

          <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "4%" }}>
              <label style={{ fontSize: 11 }}>ATM:</label>
              <MUITextField
                className={
                  siteId !== 0
                    ? classes.gridTwoBlack
                    : waterResistAtm
                    ? classes.gridTwoBlue
                    : classes.gridTwo
                }
                disabled={siteId !== 0}
                onChange={handleWaterResistChangeAtm}
                value={waterResistAtm || ""}
                style={{ width: "100%" }}
                multiline
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "4%" }}>
              <label style={{ fontSize: 11 }}>M:</label>
              <MUITextField
                className={
                  siteId !== 0
                    ? classes.gridTwoBlack
                    : waterResistM
                    ? classes.gridTwoBlue
                    : classes.gridTwo
                }
                disabled={siteId !== 0}
                onChange={handleWaterResistChangeM}
                value={waterResistM || ""}
                style={{ width: "100%" }}
                multiline
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "4%" }}>
              <label style={{ fontSize: 11 }}>Ft:</label>
              <MUITextField
                className={
                  siteId !== 0
                    ? classes.gridTwoBlack
                    : waterResistFt
                    ? classes.gridTwoBlue
                    : classes.gridTwo
                }
                disabled={siteId !== 0}
                onChange={handleWaterResistChangeFt}
                onBlur={handleWaterResistBlurFt}
                value={waterResistFt || ""}
                style={{ width: "100%" }}
                multiline
              />
            </div>
            <Box style={{ width: "4%" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!values.helium_escape_valve}
                    disabled={siteId !== 0}
                    onChange={(e) => setFieldValue("helium_escape_valve", e.target.checked)}
                    style={{ padding: 0 }}
                  />
                }
                label={<span style={{ fontSize: 11 }}>Helium:</span>}
                labelPlacement="top"
                style={{ margin: 0, gap: 0 }}
              />
            </Box>
            <div style={{ display: "flex", flexDirection: "column", gap: 1, width: "79%" }}>
              <label style={{ fontSize: 11 }}>Water Resis. Notes:</label>
              <DebouncedTextField
                className={
                  siteId !== 0
                    ? classes.helium_escape_valve_notes
                    : values.dimension_notes
                    ? classes.gridTwoBlue
                    : classes.gridTwo
                }
                disabled={siteId !== 0}
                name="helium_escape_valve_notes"
                style={{ width: "100%" }}
                multiline
              />
            </div>


          </Box>

        </Box>
      </AccordionDetails>
    </Accordion>
  );
};